import { DefaultViewBadge } from '@/features/experiences/components/DefaultViewBadge';
import { viewNameInPlanner } from '@/features/experiences/lib/viewName';
import type { ExperienceChild } from '@/features/experiences/types';
import { ExperienceChildConfigKind } from '@/types/gql.generated';
import { Box, Button, Flex, Icon, Text } from '@/ui';
import { SidebarIconChip } from '../SidebarKit';
import { useDropdown } from '../useDropdown';
import { viewIcon } from './layoutRegistry';
import { ViewMoreMenu } from './ViewMoreMenu';

// ── Child view display helpers ─────────────────────────────────────────────────

// kind (not type) is the authoritative printable signal — matches
// ViewPickerDropdown, useDateRange, and ChildPlannerView.
const isPrintableView = (child: ExperienceChild) =>
  child.kind === ExperienceChildConfigKind.Printable;

const getViewIcon = (child: ExperienceChild) =>
  viewIcon(child.layoutId, isPrintableView(child));

// ── Section header inside the dropdown ─────────────────────────────────────────

type SectionLabelProps = {
  children: string;
};

const SectionLabel = ({ children }: SectionLabelProps) => (
  <Text
    color="ink.muted"
    fontSize="ah-label"
    fontWeight={700}
    letterSpacing="ah-label"
    pb={1}
    pt={1.5}
    px={3}
    textTransform="uppercase"
  >
    {children}
  </Text>
);

// ── Single child-view row inside the dropdown ──────────────────────────────────

type ViewListItemProps = {
  child: ExperienceChild;
  // Resolved by the caller, which has the sibling list the numbering needs.
  label: string;
  isActive: boolean;
  isDefault: boolean;
  onSelect: () => void;
};

// The chip's default tint is a black wash, invisible on the dark selected/hovered
// row — lighten it so the view icon keeps its container.
const CHIP_ON_DARK = { '& [data-view-chip]': { bg: 'whiteAlpha.200' } };

const ViewListItem = ({
  child,
  label,
  isActive,
  isDefault,
  onSelect,
}: ViewListItemProps) => (
  <Box pb={0.5} px={1.5}>
    <Flex
      // The selected row reads as solid black (purple stays reserved for the
      // internal-only "Add a view" action). A non-selected row gets a light
      // neutral hover instead of black, so hover doesn't read as a selection.
      align="center"
      as="button"
      bg={isActive ? 'ink.strong' : undefined}
      borderRadius="ah-md"
      color={isActive ? 'white' : 'ink.strong'}
      cursor="pointer"
      gap={2}
      px={2}
      py={1.5}
      sx={isActive ? CHIP_ON_DARK : undefined}
      textAlign="left"
      type="button"
      w="full"
      _hover={
        isActive
          ? { bg: 'ink.strong', color: 'white', ...CHIP_ON_DARK }
          : { bg: 'rgba(0,0,0,0.04)' }
      }
      onClick={onSelect}
    >
      <SidebarIconChip data-view-chip="" size="26px">
        <Icon color="inherit" icon={getViewIcon(child)} size={3.5} />
      </SidebarIconChip>
      <Text
        color="inherit"
        flex={1}
        fontSize="ah-control"
        fontWeight={isActive ? 600 : 500}
      >
        {label}
      </Text>
      {isDefault && <DefaultViewBadge onDark={isActive} />}
      {isActive && <Icon color="inherit" icon="Checkmark" size={4} />}
    </Flex>
  </Box>
);

// ── ViewSelector ───────────────────────────────────────────────────────────────

type ViewSelectorProps = {
  activeChildId: string | undefined;
  defaultChildId?: string | null;
  isRemoving?: boolean;
  onAddView: () => void;
  onChangeLayout: () => void;
  onRemoveView: () => void;
  onSelectView: (childId: string) => void;
  onToggleDefault: () => void;
  views: ExperienceChild[];
};

export const ViewSelector = ({
  activeChildId,
  defaultChildId,
  isRemoving,
  onAddView,
  onChangeLayout,
  onRemoveView,
  onSelectView,
  onToggleDefault,
  views,
}: ViewSelectorProps) => {
  const { close, isOpen, ref, toggle } = useDropdown();

  const activeChild =
    views.find((v) => v.id === activeChildId) ?? views[0] ?? null;

  const interactiveViews = views.filter((v) => !isPrintableView(v));
  const printableViews = views.filter(isPrintableView);

  const handleSelect = (childId: string) => {
    onSelectView(childId);
    close();
  };

  return (
    <Box mb={4} position="relative" ref={ref}>
      <Button
        leftIcon={<Icon icon="Plus" size={4} />}
        mb={2}
        size="ah"
        variant="ahAccentOutline"
        w="full"
        onClick={() => {
          close();
          onAddView();
        }}
      >
        Add a view
      </Button>

      <Flex align="center" gap={2}>
        <Flex
          _hover={{ borderColor: 'rgba(0,0,0,0.16)' }}
          align="center"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          as="button"
          bg="white"
          border="1px solid"
          borderColor="line.DEFAULT"
          borderRadius="ah-xl"
          cursor="pointer"
          flex={1}
          gap={2.5}
          px={3}
          py={2.5}
          textAlign="left"
          transition="border-color 0.14s"
          type="button"
          onClick={toggle}
        >
          {activeChild && (
            <SidebarIconChip size="26px">
              <Icon
                color="ink.soft"
                icon={getViewIcon(activeChild)}
                size={3.5}
              />
            </SidebarIconChip>
          )}
          <Text
            color="ink.strong"
            flex={1}
            fontSize="ah-control"
            fontWeight={600}
          >
            {activeChild ? viewNameInPlanner(activeChild, views) : 'No views'}
          </Text>
          <Icon
            color="ink.muted"
            icon={isOpen ? 'ChevronUp' : 'ChevronDown'}
            size={4}
          />
        </Flex>

        <ViewMoreMenu
          isDefault={!!activeChild && activeChild.id === defaultChildId}
          isRemoving={isRemoving}
          onChangeLayout={onChangeLayout}
          onRemoveView={onRemoveView}
          onToggleDefault={onToggleDefault}
        />
      </Flex>

      {isOpen && (
        <Box
          bg="white"
          border="1px solid"
          borderColor="line.DEFAULT"
          borderRadius="ah-xl"
          boxShadow="0 4px 12px rgba(0,0,0,0.08)"
          left={0}
          mt={1.5}
          overflow="hidden"
          pb={1}
          position="absolute"
          pt={1}
          right={0}
          top="100%"
          zIndex={10}
        >
          {interactiveViews.length > 0 && (
            <>
              <SectionLabel>Interactive</SectionLabel>
              {interactiveViews.map((child) => (
                <ViewListItem
                  child={child}
                  isActive={child.id === activeChild?.id}
                  isDefault={child.id === defaultChildId}
                  key={child.id}
                  label={viewNameInPlanner(child, views)}
                  onSelect={() => handleSelect(child.id)}
                />
              ))}
            </>
          )}

          {printableViews.length > 0 && (
            <>
              {interactiveViews.length > 0 && (
                <Box
                  borderTopColor="line.soft"
                  borderTopStyle="solid"
                  borderTopWidth="1px"
                  mt={1}
                  pt={1}
                />
              )}
              <SectionLabel>Printable</SectionLabel>
              {printableViews.map((child) => (
                <ViewListItem
                  child={child}
                  isActive={child.id === activeChild?.id}
                  isDefault={child.id === defaultChildId}
                  key={child.id}
                  label={viewNameInPlanner(child, views)}
                  onSelect={() => handleSelect(child.id)}
                />
              ))}
            </>
          )}

          <Box
            borderTopColor="line.soft"
            borderTopStyle="solid"
            borderTopWidth="1px"
            mt={1}
            pb={1}
            pt={1}
            px={1.5}
          >
            <Flex
              _hover={{ bg: 'magicPurple.25' }}
              align="center"
              as="button"
              borderRadius="ah-md"
              cursor="pointer"
              gap={2}
              px={2}
              py={2}
              textAlign="left"
              type="button"
              w="full"
              onClick={() => {
                close();
                onAddView();
              }}
            >
              <Icon color="magicPurple.500" icon="Plus" size={4} />
              <Text
                color="magicPurple.600"
                fontSize="ah-control"
                fontWeight={600}
              >
                Add a view
              </Text>
            </Flex>
          </Box>
        </Box>
      )}
    </Box>
  );
};
